import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  FileDown,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api-client";
import {
  useReceivedDocument,
  useAcceptRejectDocuments,
  AcceptanceStatusBadge,
  DeadlineBadge,
  AcceptRejectDialog,
  DTE_TYPE_LABELS,
} from "@/features/received-documents";
import type { DteTypeCode, BatchAction } from "@/features/received-documents";

function formatCurrency(value: string | number | null): string {
  if (!value) return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(num);
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-CL", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString("es-CL", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function DetalleRecepcionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { document: doc, isLoading, error, refetch } = useReceivedDocument(id!);
  const { isProcessing, progress, processBatch, reset } =
    useAcceptRejectDocuments();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<BatchAction>("accept");

  const handleDownloadXml = async () => {
    if (!id) return;
    try {
      const blob = await apiFetch.blob(`/api/received-documents/${id}/xml`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dte-recibido-${doc?.folio ?? id}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      toast.error("Error al descargar el XML");
    }
  };

  const handleAccept = () => {
    setDialogAction("accept");
    setDialogOpen(true);
  };

  const handleReject = () => {
    setDialogAction("reject");
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (reason?: string) => {
    if (!doc) return;
    await processBatch([doc.id], [doc.folio], dialogAction, reason);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    reset();
    if (progress && progress.succeeded > 0) {
      refetch();
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        Cargando documento...
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="p-6">
        <div className="text-red-500">
          {error?.message ?? "Documento no encontrado"}
        </div>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/facturacion/recepcion")}
        >
          Volver
        </Button>
      </div>
    );
  }

  const docTypeLabel =
    DTE_TYPE_LABELS[Number(doc.documentTypeCode) as DteTypeCode] ??
    doc.documentType;

  const totals = doc.countryMetadata?.totales;
  const emisor = doc.countryMetadata?.emisor;
  const receptor = doc.countryMetadata?.receptor;
  const isPending = doc.acceptanceStatus === "pending";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/facturacion/recepcion")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">{docTypeLabel}</h1>
            <AcceptanceStatusBadge status={doc.acceptanceStatus} />
            {isPending && (
              <DeadlineBadge daysUntilDeadline={doc.daysUntilDeadline} />
            )}
          </div>
          <p className="text-muted-foreground">
            Folio: {doc.folio} • {formatDate(doc.issueDate)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button variant="outline" onClick={handleDownloadXml}>
          <FileDown className="mr-2 h-4 w-4" />
          Descargar XML
        </Button>
        {isPending && (
          <>
            <Button onClick={handleAccept}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Aceptar
            </Button>
            <Button variant="outline" onClick={handleReject}>
              <XCircle className="mr-2 h-4 w-4" />
              Rechazar
            </Button>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parties */}
          <div className="bg-card border rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Emisor (Proveedor) */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Proveedor (Emisor)
                </h3>
                <div className="text-sm">
                  <div className="font-medium">
                    {emisor?.razonSocial ?? doc.issuerName ?? "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {emisor?.rut ?? doc.issuerTaxId}
                  </div>
                  {emisor?.giro && (
                    <div className="text-muted-foreground text-xs mt-1">
                      {emisor.giro}
                    </div>
                  )}
                  {emisor?.direccion && (
                    <div className="text-muted-foreground text-xs">
                      {emisor.direccion}
                      {emisor.comuna && `, ${emisor.comuna}`}
                    </div>
                  )}
                </div>
              </div>

              {/* Receptor (Nosotros) */}
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Receptor
                </h3>
                <div className="text-sm">
                  <div className="font-medium">
                    {receptor?.razonSocial ?? "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {receptor?.rut ?? "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Line Items */}
          {doc.countryMetadata?.detalles &&
            doc.countryMetadata.detalles.length > 0 && (
              <div className="bg-card border rounded-lg p-6">
                <h3 className="text-sm font-medium mb-4">Detalle</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Descripción</th>
                      <th className="text-right py-2">Cant.</th>
                      <th className="text-right py-2">P. Unit.</th>
                      <th className="text-right py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.countryMetadata.detalles.map((item, idx) => (
                      <tr key={idx} className="border-b last:border-b-0">
                        <td className="py-2">{item.nombre}</td>
                        <td className="text-right py-2">{item.cantidad}</td>
                        <td className="text-right py-2">
                          {formatCurrency(item.precioUnitario)}
                        </td>
                        <td className="text-right py-2">
                          {formatCurrency(item.montoItem)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          {/* Totals */}
          <div className="bg-card border rounded-lg p-6">
            <h3 className="text-sm font-medium mb-4">Resumen</h3>
            <div className="space-y-2 text-sm">
              {totals?.montoNeto && totals.montoNeto > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Neto:</span>
                  <span>{formatCurrency(totals.montoNeto)}</span>
                </div>
              )}
              {totals?.montoExento && totals.montoExento > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Exento:</span>
                  <span>{formatCurrency(totals.montoExento)}</span>
                </div>
              )}
              {totals?.iva && totals.iva > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">IVA 19%:</span>
                  <span>{formatCurrency(totals.iva)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t font-medium">
                <span>Total:</span>
                <span className="text-lg">
                  {formatCurrency(doc.totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Reception Info */}
          <div className="bg-card border rounded-lg p-6">
            <h3 className="text-sm font-medium mb-4">
              Información de Recepción
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-muted-foreground">Recibido vía</div>
                  <div className="font-medium capitalize">
                    {doc.receivedVia}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-muted-foreground">
                    Fecha de recepción
                  </div>
                  <div className="font-medium">
                    {formatDateTime(doc.receivedAt)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Acceptance Timeline */}
          <div className="bg-card border rounded-lg p-6">
            <h3 className="text-sm font-medium mb-4">Historial</h3>
            <div className="space-y-4">
              {/* Received */}
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                <div>
                  <div className="text-sm font-medium">Recibido</div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(doc.receivedAt)}
                  </div>
                </div>
              </div>

              {/* Accepted */}
              {doc.acceptedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2" />
                  <div>
                    <div className="text-sm font-medium">Aceptado</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(doc.acceptedAt)}
                    </div>
                  </div>
                </div>
              )}

              {/* Rejected */}
              {doc.rejectedAt && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-2" />
                  <div>
                    <div className="text-sm font-medium">Rechazado</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateTime(doc.rejectedAt)}
                    </div>
                    {doc.rejectionReason && (
                      <div className="text-xs mt-1 p-2 bg-red-50 rounded">
                        {doc.rejectionReason}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Pending */}
              {isPending && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 animate-pulse" />
                  <div>
                    <div className="text-sm font-medium">
                      Pendiente de decisión
                    </div>
                    {doc.daysUntilDeadline !== null && (
                      <div className="text-xs text-muted-foreground">
                        {doc.daysUntilDeadline > 0
                          ? `${doc.daysUntilDeadline} días restantes`
                          : "Plazo vencido"}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SII Tracking */}
          {doc.siiClaimTrackingId && (
            <div className="bg-card border rounded-lg p-6">
              <h3 className="text-sm font-medium mb-2">Track ID SII</h3>
              <code className="text-sm bg-muted px-2 py-1 rounded">
                {doc.siiClaimTrackingId}
              </code>
            </div>
          )}
        </div>
      </div>

      {/* Accept/Reject Dialog */}
      <AcceptRejectDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && !isProcessing) {
            handleDialogClose();
          }
        }}
        documents={[
          {
            id: doc.id,
            documentType: doc.documentType,
            documentTypeCode: doc.documentTypeCode,
            folio: doc.folio,
            issueDate: doc.issueDate,
            issuerTaxId: doc.issuerTaxId,
            issuerName: doc.issuerName,
            netAmount: doc.netAmount,
            taxAmount: doc.taxAmount,
            totalAmount: doc.totalAmount,
            acceptanceStatus: doc.acceptanceStatus,
            receivedAt: doc.receivedAt,
            daysUntilDeadline: doc.daysUntilDeadline,
          },
        ]}
        action={dialogAction}
        isProcessing={isProcessing}
        progress={progress}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </div>
  );
}
